import React, { useState, useEffect } from 'react';
import { FaTrash, FaPlus, FaEdit, FaGripVertical, FaCopy } from 'react-icons/fa';
import { Language_Text_Editor } from './language_text_editor.js';
import { Section_Editor } from './section_editor.js';
import {
    DragDropContext,
    Droppable,
    Draggable
} from 'react-beautiful-dnd';
import { Colors } from './form_builder.js';
import { useTranslation } from 'react-i18next';
import * as menu from "../../../assets/menus/menu.js";
import { formBuilderLabels } from '../../_label/form_builder/form_builder_labels.js';

/**
 * Sections_Builder with drag-and-drop
 */
const Sections_Builder = ({
    sections,
    language,
    onTitleChange,
    onDelete,
    onAdd,
    onUpdateSection,
    onReorder,
    onClone,
    colors = Colors
}) => {
    const [editingIndex, setEditingIndex] = useState(null);
    const { t } = useTranslation();

    // Initialize translations from centralized file
    useEffect(() => {
        const i18n = menu.getI18n();
        i18n.addResourceBundle('it', 'labels', formBuilderLabels.it, true, true);
        i18n.addResourceBundle('en', 'labels', formBuilderLabels.en, true, true);
    }, []);

    const handleConfirmEdit = (updatedSection) => {
        onUpdateSection(editingIndex, updatedSection);
        setEditingIndex(null);
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const reordered = Array.from(sections);
        const [moved] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, moved);

        onReorder(reordered);
    };

    if (editingIndex !== null) {
        return (
            <Section_Editor
                section={sections[editingIndex]}
                language={language}
                onConfirm={handleConfirmEdit}
                formSections={sections}
                colors={colors}
                isEditing={true}
            />
        );
    }

    return (
        <div className="card shadow-sm w-100">
            <div className="card-header d-flex justify-content-between align-items-center" 
                style={{ backgroundColor: colors.secondaryLight }}>
                <h5 className="mb-0">{t('formSections', { ns: 'labels' })}</h5>
                <button 
                    className="btn btn-sm" 
                    style={{ 
                        backgroundColor: colors.white, 
                        color: colors.dark 
                    }}
                    onClick={onAdd}
                    title={t('addSection', { ns: 'labels' })}
                >
                    <FaPlus className="me-1" />
                    {t('addSection', { ns: 'labels' })}
                </button>
            </div>
            
            <div className="card-body p-3">
                {sections.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                        <p>{t('noSections', { ns: 'labels' })}</p>
                    </div>
                ) : (
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="sections">
                            {(provided, snapshot) => (
                                <div 
                                    ref={provided.innerRef} 
                                    {...provided.droppableProps}
                                    className="sections-list w-100"
                                    style={{
                                        transition: 'background-color 0.2s ease',
                                        backgroundColor: snapshot.isDraggingOver ? colors.primaryLight : 'transparent'
                                    }}
                                >
                                    {sections.map((section, index) => (
                                        <Draggable
                                            key={`section-${section.id || index}`}
                                            draggableId={`section-${section.id || index}`}
                                            index={index}
                                        >
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    className={`card mb-2 w-100 ${snapshot.isDragging ? 'border-primary' : ''}`}
                                                    style={{
                                                        ...provided.draggableProps.style,
                                                        transition: snapshot.isDragging ? 'transform 0.2s ease, box-shadow 0.2s ease' : 'none',
                                                        ...(snapshot.isDragging ? { 
                                                            borderColor: colors.primary,
                                                            cursor: 'grabbing',
                                                            boxShadow: '0 5px 15px rgba(0,0,0,0.15)',
                                                            zIndex: 1000
                                                        } : {
                                                            transform: 'none',
                                                            transition: 'none'
                                                        }),
                                                        // Apply reduced opacity for invisible sections
                                                        opacity: section.invisible ? 0.6 : 1,
                                                    }}
                                                >
                                                    <div className="card-body py-2 px-3">
                                                        <div className="row align-items-center w-100 g-0">
                                                            <div className="col-auto" {...provided.dragHandleProps}>
                                                                <FaGripVertical className="text-muted" />
                                                            </div>
                                                            <div className="col">
                                                                <Language_Text_Editor
                                                                    language={language}
                                                                    mode="input"
                                                                    text={section.name}
                                                                    onChange={(updatedNames) => onTitleChange(index, updatedNames)}
                                                                    colors={colors}
                                                                />
                                                                <div className="mt-1 text-muted small">
                                                                    {t('questions', { ns: 'labels' })}: <strong>{section.questions?.length || 0}</strong>
                                                                    {section.invisible !== undefined && (
                                                                        <span className="ms-3">{t('invisible', { ns: 'labels' })}: <strong>{section.invisible ? t('yes', { ns: 'labels' }) : t('no', { ns: 'labels' })}</strong></span>
                                                                    )}
                                                                    {section.sectionCode && (
                                                                        <span className="ms-3">{t('code', { ns: 'labels' })}: <strong>{section.sectionCode}</strong></span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="col-auto ms-3">
                                                                <div className="btn-group">
                                                                    <button
                                                                        className="btn btn-sm"
                                                                        style={{ 
                                                                            color: colors.info,
                                                                            borderColor: colors.info,
                                                                            backgroundColor: colors.white 
                                                                        }}
                                                                        title={t('editSection', { ns: 'labels' })}
                                                                        onClick={() => {
                                                                            // Save any pending local changes before entering editor
                                                                            // The onTitleChange should have already been called by Language_Text_Editor
                                                                            setEditingIndex(index);
                                                                        }}
                                                                    >
                                                                        <FaEdit />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm"
                                                                        style={{ 
                                                                            color: colors.success,
                                                                            borderColor: colors.success,
                                                                            backgroundColor: colors.white 
                                                                        }}
                                                                        title={t('cloneSection', { ns: 'labels' })}
                                                                        onClick={() => onClone(index)}
                                                                    >
                                                                        <FaCopy />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm"
                                                                        style={{ 
                                                                            color: colors.danger,
                                                                            borderColor: colors.danger,
                                                                            backgroundColor: colors.white 
                                                                        }}
                                                                        title={t('deleteSection', { ns: 'labels' })}
                                                                        onClick={() => onDelete(index)}
                                                                    >
                                                                        <FaTrash />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                )}
            </div>
        </div>
    );
};

export { Sections_Builder };
